// Copy for /our-work/context-engineering (src/pages/OurWorkContextEngineering.tsx).
// Layer icons/ids and output emojis stay in the page; arrays here are matched by index.

const en = {
  seo: {
    title: 'Context Engineering for AI | Stratodata',
    description:
      "Raw data doesn't make AI reliable — context does. We structure and enrich retail data so AI models produce accurate, consistent and actionable analysis every time.",
  },
  hero: {
    title: 'Context Engineering',
    lead: 'Quality AI starts with quality context.',
    sub: 'We capture your business reality so AI models understand your world the same way you do.',
    cta: 'Let\'s build reliable AI',
  },
  problem: {
    titleBefore: 'Do not just get ',
    titleEmphasis: 'any',
    titleAfter: ' answers.',
    accent: 'Obtain AI reliability.',
    text: "Generic AI gives generic answers. It doesn't know your margins, your channels, your product hierarchy, or your North Star. Context Engineering bridges that gap — permanently.",
  },
  comparison: {
    title: 'The difference context makes',
    sub: 'Same question. Same AI model. Completely different value.',
    quoteOpen: '"',
    quoteClose: '"',
    withoutLabel: 'Without context',
    withLabel: 'With Stratodata context',
    items: [
      {
        question: 'What was our profit last month?',
        without: '"Based on the data, your profit was approximately €142,000. Note: this figure may vary depending on how profit is calculated."',
        with: '"Net Contribution for March was €141,832 across all 3 sales channels. This is down 4.2% vs February, driven primarily by a 2.1pp increase in return rate in the Sneakers category on the DE market."',
      },
      {
        question: 'Should we increase ad spend on Google?',
        without: '"Increasing ad spend could potentially improve visibility and sales, though results may vary depending on your targeting and budget allocation."',
        with: '"Google Ads profit-per-click is €0.42 this month, above your €0.30 threshold. However, the marginal return has been declining since week 3. I recommend increasing spend by max 15% and reviewing after 7 days against your NC target."',
      },
      {
        question: 'Which products should we discontinue?',
        without: '"You could consider discontinuing products with low sales volume or negative reviews. Would you like me to analyze your product catalog?"',
        with: '"12 SKUs scored \'Discontinue\' this quarter: all below 25% margin, under 50 units/month, and with return rates above 20%. Combined, they consume €8,400/month in warehouse cost for €2,100 net contribution. Here\'s the list ranked by drag on blended margin."',
      },
    ],
  },
  layers: {
    title: '5 layers of context we build',
    sub: 'Each layer adds depth. Together, they create an AI that truly knows your business.',
    exampleLabel: 'Example',
    items: [
      {
        title: 'Business Glossary',
        short: 'We define your world in machine-readable language.',
        description: 'Every company uses words differently. "Revenue" might mean order value, net sales, or gross profit depending on who you ask. We build a precise glossary that defines every metric, dimension, and business rule — so AI never guesses.',
        examples: ['"Net Sales" = Order Value minus Returns minus Cancellations, per sales channel, in local currency.', '"Active Customer" = placed at least 1 order in last 365 days with status Delivered.'],
      },
      {
        title: 'Metric Logic & Hierarchies',
        short: 'We map how your numbers relate to each other.',
        description: 'AI needs to understand that Gross Margin depends on Net Sales and COGS, that Net Sales sits below Order Value, that MIR is marketing spend divided by net sales. We encode the entire Walk-to-Profit logic, category hierarchies, channel structures, and attribution rules.',
        examples: ['Walk to Profit cascade: Order Value → Net Sales → Gross Margin → MKTG Profit → NC → EBIT', 'Product hierarchy: Department → Category → Subcategory → SKU, with margin targets at each level.'],
      },
      {
        title: 'Guardrails & Boundaries',
        short: 'We teach AI what it should and shouldn\'t do.',
        description: 'Context Engineering isn\'t just about knowledge — it\'s about constraints. We define which questions the AI can answer confidently, where it should flag uncertainty, and when to escalate to a human. This is what separates reliable AI from hallucinating AI.',
        examples: ['If a query crosses data boundaries (e.g. mixing YTD and rolling 12M), flag it instead of blending silently.', 'Never extrapolate margin from a single channel to the full business. Always caveat scope.'],
      },
      {
        title: 'Question Library',
        short: 'We pre-build the questions your team should be asking.',
        description: 'Most teams don\'t know what to ask AI. We build a curated library of high-value questions — tested, validated, and ready to run. From daily operational checks to strategic deep dives, your team gets instant value from day one.',
        examples: ['"What are the top 5 SKUs dragging down my blended gross margin this month vs. last?"', '"Which marketing channel has the best profit-per-click this week, and is the trend improving?"'],
      },
      {
        title: 'Model Connections',
        short: 'We plug context into every AI touchpoint.',
        description: 'Context is useless if it stays in a document. We inject it into every AI interface your team uses — Claude via MCP, in-app chatbots, Microsoft Copilot in Teams, custom dashboards. The context travels with the data, everywhere.',
        examples: ['MCP server that serves business context alongside live data to Claude Desktop.', 'System prompts for your in-app chatbot that include metric definitions, current targets, and known data caveats.'],
      },
    ],
  },
  flow: {
    title: 'Context flows into every AI touchpoint',
    sourceTitle: 'Your Business Reality',
    sourceSub: 'Metrics, rules, hierarchies, targets, caveats',
    engineSide: 'We capture & encode it',
    engineTitle: 'Stratodata Context Engine',
    engineSub: 'Business glossary, metric logic, guardrails, question library',
    outputs: [
      { title: 'Claude via MCP', desc: 'Natural language analytics with full business context' },
      { title: 'In-app Chatbot', desc: 'Your own AI assistant embedded in RAS dashboards' },
      { title: 'Copilot in Teams', desc: 'Context-aware answers inside the tools your team already uses' },
    ],
  },
  stats: [
    { stat: '10x', label: 'more precise answers', sub: 'vs. generic AI prompts' },
    { stat: '0', label: 'hallucinations on defined metrics', sub: 'guardrails catch ambiguity' },
    { stat: 'Day 1', label: 'value from question library', sub: 'no learning curve for your team' },
  ],
  process: {
    eyebrow: 'How we work',
    title: 'From your knowledge to AI understanding',
    phases: [
      {
        phase: 'Week 1–2',
        title: 'Discovery & Capture',
        description: 'We sit with your team — finance, marketing, operations — and extract every metric definition, business rule, and tribal knowledge that lives in people\'s heads. We document what "good" looks like for your business.',
      },
      {
        phase: 'Week 2–3',
        title: 'Encode & Structure',
        description: 'We translate captured knowledge into machine-readable context: structured glossaries, metric hierarchies, guardrail rules, and a curated question library. Everything is version-controlled and auditable.',
      },
      {
        phase: 'Week 3–4',
        title: 'Connect & Validate',
        description: 'We inject context into your AI touchpoints — MCP servers, chatbot system prompts, agent configurations. Then we validate: your team asks real questions, we compare answers against expected outcomes, and we refine until precision is bulletproof.',
      },
      {
        phase: 'Ongoing',
        title: 'Evolve & Maintain',
        description: 'Your business changes. New products, new channels, new targets. We keep context in sync — updating definitions, adding new questions, tuning guardrails. Context Engineering is a living practice, not a one-time project.',
      },
    ],
  },
  cta: {
    title: 'Make your AI reliable',
    text: 'Stop getting generic answers. Start getting answers that know your business as well as you do.',
    button: "Let's talk Context Engineering",
  },
};

const sk: typeof en = {
  seo: {
    title: 'Context Engineering pre AI | Stratodata',
    description:
      'Spoľahlivou robí AI kontext, samotné surové dáta na to nestačia. Retailové dáta štruktúrujeme a obohacujeme tak, aby AI modely zakaždým dávali presné, konzistentné a použiteľné analýzy.',
  },
  hero: {
    title: 'Context Engineering',
    lead: 'Kvalita AI začína kvalitou kontextu.',
    sub: 'Zachytíme realitu vášho biznisu, aby AI modely rozumeli vášmu svetu rovnako ako vy.',
    cta: 'Postavme spoľahlivú AI',
  },
  problem: {
    titleBefore: 'Neuspokojte sa s ',
    titleEmphasis: 'hocijakými',
    titleAfter: ' odpoveďami.',
    accent: 'Získajte spoľahlivú AI.',
    text: 'Všeobecná AI dáva všeobecné odpovede. Nepozná vaše marže, kanály, produktovú hierarchiu ani vašu North Star metriku. Context Engineering tento rozdiel odstráni natrvalo.',
  },
  comparison: {
    title: 'Aký rozdiel robí kontext',
    sub: 'Rovnaká otázka. Rovnaký AI model. Úplne iná hodnota.',
    quoteOpen: '„',
    quoteClose: '“',
    withoutLabel: 'Bez kontextu',
    withLabel: 'S kontextom od Stratodata',
    items: [
      {
        question: 'Aký bol náš zisk minulý mesiac?',
        without: '„Na základe dát bol váš zisk približne 142 000 €. Poznámka: toto číslo sa môže líšiť podľa toho, ako sa zisk počíta.“',
        with: '„Čistý príspevok na úhradu za marec bol 141 832 € naprieč všetkými 3 predajnými kanálmi. Oproti februáru je to pokles o 4,2 %, spôsobený najmä nárastom miery vratiek o 2,1 p. b. v kategórii Tenisky na trhu DE.“',
      },
      {
        question: 'Máme zvýšiť výdavky na reklamu v Googli?',
        without: '„Zvýšenie výdavkov na reklamu by mohlo zlepšiť viditeľnosť a predaj, výsledky sa však môžu líšiť v závislosti od cielenia a rozdelenia rozpočtu.“',
        with: '„Zisk na klik v Google Ads je tento mesiac 0,42 €, teda nad vaším prahom 0,30 €. Marginálna návratnosť však od 3. týždňa klesá. Odporúčam zvýšiť výdavky maximálne o 15 % a po 7 dňoch ich vyhodnotiť voči vášmu cieľu čistého príspevku na úhradu.“',
      },
      {
        question: 'Ktoré produkty by sme mali vyradiť?',
        without: '„Môžete zvážiť vyradenie produktov s nízkym objemom predaja alebo negatívnymi recenziami. Chcete, aby som analyzoval váš produktový katalóg?“',
        with: '„Tento štvrťrok dostalo hodnotenie ‚Vyradiť‘ 12 SKU: všetky majú maržu pod 25 %, predaj pod 50 ks mesačne a mieru vratiek nad 20 %. Spolu stoja 8 400 € mesačne na skladových nákladoch a prinesú čistý príspevok na úhradu len 2 100 €. Tu je zoznam zoradený podľa toho, ako veľmi sťahujú celkovú maržu.“',
      },
    ],
  },
  layers: {
    title: '5 vrstiev kontextu, ktoré staviame',
    sub: 'Každá vrstva pridáva hĺbku. Spolu z nich vznikne AI, ktorá váš biznis naozaj pozná.',
    exampleLabel: 'Príklad',
    items: [
      {
        title: 'Biznisový slovník',
        short: 'Váš svet popíšeme jazykom, ktorému rozumie stroj.',
        description: 'Každá firma používa slová inak. „Tržby“ môžu znamenať hodnotu objednávok, čisté tržby alebo hrubý zisk, podľa toho, koho sa spýtate. Zostavíme presný slovník, ktorý definuje každú metriku, dimenziu a biznisové pravidlo. AI potom nemusí nič hádať.',
        examples: ['„Čisté tržby“ = hodnota objednávok mínus vratky mínus storná, za každý predajný kanál, v lokálnej mene.', '„Aktívny zákazník“ = za posledných 365 dní urobil aspoň 1 objednávku v stave Doručená.'],
      },
      {
        title: 'Logika a hierarchie metrík',
        short: 'Zmapujeme, ako spolu vaše čísla súvisia.',
        description: 'AI musí chápať, že hrubá marža závisí od čistých tržieb a nákladov na predaný tovar (COGS), že čisté tržby sú pod hodnotou objednávok a že MIR sú marketingové výdavky delené čistými tržbami. Zakódujeme celú logiku Walk-to-Profit, kategóriové hierarchie, štruktúru kanálov aj pravidlá atribúcie.',
        examples: ['Walk to Profit cascade: hodnota objednávok → čisté tržby → hrubá marža → MKTG zisk → čistý príspevok → EBIT', 'Produktová hierarchia: oddelenie → kategória → podkategória → SKU, s cieľovou maržou na každej úrovni.'],
      },
      {
        title: 'Mantinely a hranice',
        short: 'Naučíme AI, čo robiť má a čo nie.',
        description: 'Context Engineering nie je len o znalostiach. Ide aj o obmedzenia. Určíme, na ktoré otázky môže AI odpovedať s istotou, kde má priznať neistotu a kedy má otázku posunúť človeku. Práve tým sa spoľahlivá AI líši od AI, ktorá si vymýšľa.',
        examples: ['Ak otázka prekračuje hranice dát (napr. mieša YTD a kĺzavých 12 mesiacov), upozorniť na to namiesto toho, aby čísla potichu zmiešala.', 'Nikdy neextrapolovať maržu z jedného kanála na celý biznis. Vždy uviesť, za aký rozsah odpoveď platí.'],
      },
      {
        title: 'Knižnica otázok',
        short: 'Vopred pripravíme otázky, ktoré by si mal váš tím klásť.',
        description: 'Väčšina tímov nevie, čo sa AI pýtať. Zostavíme knižnicu hodnotných otázok, ktoré sú otestované, overené a pripravené na použitie. Od denných prevádzkových kontrol po strategické analýzy. Váš tím z nich má úžitok od prvého dňa.',
        examples: ['„Ktorých 5 SKU tento mesiac najviac sťahuje moju celkovú hrubú maržu oproti minulému mesiacu?“', '„Ktorý marketingový kanál má tento týždeň najlepší zisk na klik a zlepšuje sa trend?“'],
      },
      {
        title: 'Prepojenie s modelmi',
        short: 'Kontext zapojíme do každého AI nástroja.',
        description: 'Kontext je zbytočný, ak ostane v dokumente. Vložíme ho do každého AI rozhrania, ktoré váš tím používa: Claude cez MCP, chatboty v aplikácii, Microsoft Copilot v Teams aj vlastné dashboardy. Kontext ide s dátami všade.',
        examples: ['MCP server, ktorý do Claude Desktop posiela biznisový kontext spolu so živými dátami.', 'Systémové prompty pre váš chatbot v aplikácii s definíciami metrík, aktuálnymi cieľmi a známymi obmedzeniami dát.'],
      },
    ],
  },
  flow: {
    title: 'Kontext sa dostane do každého AI nástroja',
    sourceTitle: 'Realita vášho biznisu',
    sourceSub: 'Metriky, pravidlá, hierarchie, ciele, obmedzenia',
    engineSide: 'Zachytíme ju a zakódujeme',
    engineTitle: 'Stratodata Context Engine',
    engineSub: 'Biznisový slovník, logika metrík, mantinely, knižnica otázok',
    outputs: [
      { title: 'Claude cez MCP', desc: 'Analytika v prirodzenom jazyku s plným biznisovým kontextom' },
      { title: 'Chatbot v aplikácii', desc: 'Vlastný AI asistent priamo v dashboardoch RAS' },
      { title: 'Copilot v Teams', desc: 'Odpovede s kontextom priamo v nástrojoch, ktoré váš tím už používa' },
    ],
  },
  stats: [
    { stat: '10x', label: 'presnejšie odpovede', sub: 'oproti všeobecným AI promptom' },
    { stat: '0', label: 'halucinácií pri definovaných metrikách', sub: 'mantinely zachytia nejednoznačnosť' },
    { stat: '1. deň', label: 'prínos z knižnice otázok', sub: 'váš tím sa nemusí nič zaúčať' },
  ],
  process: {
    eyebrow: 'Ako pracujeme',
    title: 'Od vašich znalostí k porozumeniu AI',
    phases: [
      {
        phase: '1. až 2. týždeň',
        title: 'Zmapovanie a zachytenie',
        description: 'Sadneme si s vaším tímom z financií, marketingu a prevádzky. Vytiahneme každú definíciu metriky, biznisové pravidlo aj know-how, ktoré je len v hlavách ľudí. Spíšeme, ako vo vašom biznise vyzerá dobrý výsledok.',
      },
      {
        phase: '2. až 3. týždeň',
        title: 'Zakódovanie a štruktúra',
        description: 'Zachytené znalosti preložíme do kontextu, ktorému rozumie stroj. Vzniknú štruktúrované slovníky, hierarchie metrík, pravidlá pre mantinely a knižnica otázok. Všetko je verziované a dá sa spätne skontrolovať.',
      },
      {
        phase: '3. až 4. týždeň',
        title: 'Prepojenie a overenie',
        description: 'Kontext vložíme do vašich AI nástrojov, teda do MCP serverov, systémových promptov chatbotov a nastavení AI agentov. Potom ho overíme. Váš tím kladie reálne otázky, odpovede porovnávame s očakávanými a dolaďujeme, kým presne nesedia.',
      },
      {
        phase: 'Priebežne',
        title: 'Vývoj a údržba',
        description: 'Váš biznis sa mení. Pribúdajú produkty, kanály aj ciele. Kontext preto priebežne aktualizujeme, upravujeme definície, pridávame otázky a dolaďujeme mantinely. Context Engineering je živá prax, nie jednorazový projekt.',
      },
    ],
  },
  cta: {
    title: 'Nech je vaša AI spoľahlivá',
    text: 'Namiesto všeobecných odpovedí dostanete odpovede, ktoré poznajú váš biznis tak dobre ako vy.',
    button: 'Porozprávajme sa o Context Engineeringu',
  },
};

export const contextEngineeringCopy = { en, sk };
